import React, { useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Paperclip,
  Plus,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Sortable,
  SortableItem,
  SortableItemHandle,
  arrayMove,
} from 'components/DesignSystems/Sortable/Sortable';
import ActionStatusSelect from 'components/Actions/ActionStatusSelect';

export type ChecklistItem = {
  id: string;
  label: string;
  status: string;
  notes: string;
  files: File[];
};

export const CHECKLIST_DONE_STATUS = 'Closed';
const CHECKLIST_DEFAULT_STATUS = 'Planned';

export const createChecklistItem = (
  changes: Partial<ChecklistItem> = {}
): ChecklistItem => ({
  id: crypto.randomUUID(),
  label: '',
  status: CHECKLIST_DEFAULT_STATUS,
  notes: '',
  files: [],
  ...changes,
});

const DONUT_SIZE = 25;
const DONUT_STROKE = 4;
const DONUT_RADIUS = (DONUT_SIZE - DONUT_STROKE) / 2;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

type CompletionDonutProps = {
  closed: number;
  total: number;
};

const CompletionDonut: React.FC<CompletionDonutProps> = ({ closed, total }) => {
  const fraction = total > 0 ? closed / total : 0;
  return (
    <div
      className="tw:flex tw:items-center tw:gap-2"
      role="img"
      aria-label={`Sub-task completion: ${closed} of ${total} closed`}
    >
      <svg
        width={DONUT_SIZE}
        height={DONUT_SIZE}
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        className="tw:-rotate-90"
        aria-hidden="true"
      >
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={DONUT_RADIUS}
          fill="none"
          strokeWidth={DONUT_STROKE}
          className="tw:stroke-muted"
        />
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={DONUT_RADIUS}
          fill="none"
          strokeWidth={DONUT_STROKE}
          strokeLinecap="butt"
          strokeDasharray={`${fraction * DONUT_CIRCUMFERENCE} ${DONUT_CIRCUMFERENCE}`}
          className="tw:stroke-white tw:transition-[stroke-dasharray]"
        />
      </svg>
      <div className="tw:flex tw:flex-col tw:leading-tight">
        <span className="tw:text-xs tw:text-muted-foreground">
          SUB-TASK COMPLETION
        </span>
        <span className="tw:text-sm">
          {closed}/{total}
        </span>
      </div>
    </div>
  );
};

type ChecklistRowProps = {
  item: ChecklistItem;
  index: number;
  editable: boolean;
  onChange: (changes: Partial<ChecklistItem>) => void;
  onRemove: () => void;
};

const ChecklistRow: React.FC<ChecklistRowProps> = ({
  item,
  index,
  editable,
  onChange,
  onRemove,
}) => {
  const [notesOpen, setNotesOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const position = index + 1;

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    if (selected.length > 0) onChange({ files: [...item.files, ...selected] });
    event.target.value = '';
  };

  return (
    <SortableItem
      id={item.id}
      className="tw:flex tw:flex-col tw:gap-2 tw:rounded tw:bg-background tw:data-dragging:relative tw:data-dragging:z-10 tw:data-dragging:shadow-lg"
    >
      <div className="tw:flex tw:items-center tw:gap-2">
        {editable && (
          <SortableItemHandle
            aria-label={`Reorder sub-task ${position}`}
            className="tw:flex tw:text-muted-foreground"
          >
            <GripVertical className="tw:size-4" />
          </SortableItemHandle>
        )}
        <span className="tw:w-6 tw:shrink-0 tw:text-sm tw:text-muted-foreground">
          {position}.
        </span>
        <div className="tw:w-44 tw:shrink-0">
          <ActionStatusSelect
            className="tw:w-full"
            aria-label={`Sub-task ${position} status`}
            value={item.status || CHECKLIST_DEFAULT_STATUS}
            onChange={(status) => onChange({ status })}
          />
        </div>
        <Input
          aria-label={`Sub-task ${position} name`}
          placeholder="Enter sub-task name"
          value={item.label}
          disabled={!editable}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        {editable && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Remove sub-task ${position}`}
            onClick={onRemove}
          >
            <Trash2 />
          </Button>
        )}
      </div>
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={notesOpen}
          onClick={() => setNotesOpen((open) => !open)}
        >
          <StickyNote data-icon="inline-start" />
          Notes
          {notesOpen ? (
            <ChevronUp data-icon="inline-end" />
          ) : (
            <ChevronDown data-icon="inline-end" />
          )}
        </Button>
      </div>
      {notesOpen && (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <Textarea
            aria-label={`Sub-task ${position} notes`}
            placeholder="Add notes"
            value={item.notes}
            onChange={(e) => onChange({ notes: e.target.value })}
          />
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip data-icon="inline-start" />
              Add files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={handleFilesSelected}
            />
          </div>
          {item.files.length > 0 && (
            <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0">
              {item.files.map((file, fileIndex) => (
                <li
                  key={`${file.name}-${fileIndex}`}
                  className="tw:flex tw:items-center tw:gap-2 tw:text-sm"
                >
                  <span className="tw:min-w-0 tw:flex-1 tw:truncate">
                    {file.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      onChange({
                        files: item.files.filter((_, idx) => idx !== fileIndex),
                      })
                    }
                  >
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SortableItem>
  );
};

type ActionChecklistProps = {
  checklist?: ChecklistItem[];
  setChecklist: (checklist: ChecklistItem[]) => void;
  /** When true items can be added, renamed, reordered and removed. */
  editable?: boolean;
};

const ActionChecklist: React.FC<ActionChecklistProps> = ({
  checklist = [],
  setChecklist,
  editable = false,
}) => {
  const updateItem = (id: string, changes: Partial<ChecklistItem>) => {
    setChecklist(
      checklist.map((item) => (item.id === id ? { ...item, ...changes } : item))
    );
  };

  const addItem = () => {
    setChecklist([...checklist, createChecklistItem()]);
  };

  const removeItem = (id: string) => {
    setChecklist(checklist.filter((item) => item.id !== id));
  };

  const closedCount = checklist.filter(
    (item) => item.status === CHECKLIST_DONE_STATUS
  ).length;

  const reorderItems = (oldIndex: number, newIndex: number) => {
    setChecklist(arrayMove(checklist, oldIndex, newIndex));
  };

  return (
    <div className="mt-3 rounded border border-neutral-500 p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div>
          <h4 className="tw:m-0 tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
            Sub-Tasks
          </h4>
        </div>
        <div className="tw:flex tw:items-center tw:gap-4">
          {checklist.length !== 0 && (
            <CompletionDonut closed={closedCount} total={checklist.length} />
          )}
          {editable && (
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              <Plus data-icon="inline-start" />
              Add sub-task
            </Button>
          )}
        </div>
      </div>
      {checklist.length > 0 && (
        <Sortable
          items={checklist.map((item) => item.id)}
          onReorder={reorderItems}
        >
          <div className="tw:flex tw:flex-col tw:gap-3">
            {checklist.map((item, index) => (
              <ChecklistRow
                key={item.id}
                item={item}
                index={index}
                editable={editable}
                onChange={(changes) => updateItem(item.id, changes)}
                onRemove={() => removeItem(item.id)}
              />
            ))}
          </div>
        </Sortable>
      )}
    </div>
  );
};

export default ActionChecklist;
